import React, { memo, useCallback, useState, useEffect, ReactElement } from "react";

// components
import TipsWrapper from "./components/TipsWrapper";
import TipInputBox from "./components/TipInputBox";

// constants
import { OTHER_AMOUNT } from "./constants";

// helpers
import { isOtherAmount, getErrorInfo } from "./helpers";

// interfaces
import { OnTipBoxSelectionParams, TipOption, TipWarnMessage, TipErrorInfo } from "@swiggy-private/cart-types";

export interface TippingWidgetProps {
    tipOptions: TipOption[];
    otherAmount?: string;
    isOtherSelected: boolean;
    selectedAmount?: number;
    minValidTipAmount?: number;
    maxValidTipAmount?: number;
    tipWarnMessage?: TipWarnMessage;
    className?: string;
    onUpdateTipAmount?: (amount: number) => void;
    onCustomAmountEnter?: (amount: number) => void;
    onTipBoxSelection?: (args: OnTipBoxSelectionParams) => void;
    onInputBlur?: VoidFunction;
    onInputFocus?: VoidFunction;
    inputScrollIntoView?: boolean | number;
    clickPredefinedTipAmount: (optIn: boolean, tipAmount: number) => void;
    clickCustomTipAmount: (optIn: boolean, tipAmount: number) => void;
    optInBool: boolean;
    setErrorInfo: (errorInfo: TipErrorInfo) => void;
    error: TipErrorInfo;
}

// TODO: Rewrite tiping widget after 100% UX4 migration
const TippingWidget = ({
    tipOptions,
    className,
    selectedAmount = 0,
    otherAmount = "",
    isOtherSelected,
    minValidTipAmount,
    maxValidTipAmount,
    tipWarnMessage,
    onUpdateTipAmount,
    onInputBlur,
    onInputFocus,
    inputScrollIntoView,
    clickPredefinedTipAmount,
    clickCustomTipAmount,
    optInBool,
    setErrorInfo,
    error,
}: TippingWidgetProps): ReactElement => {
    const [canShowOther, setCanShowOther] = useState(isOtherSelected);
    const [selectedTipAmount, setSelectedTipAmount] = useState(selectedAmount);

    useEffect(() => setSelectedTipAmount(selectedAmount), [selectedAmount]);
    useEffect(() => setCanShowOther(isOtherSelected), [isOtherSelected]);

    const validateSelectedTipAmount = useCallback(
        (amount = 0, updatedAmount: null | number = null) => {
            if (updatedAmount !== null) {
                const errorState = getErrorInfo(updatedAmount, tipWarnMessage, minValidTipAmount, maxValidTipAmount);
                if (errorState?.show) {
                    setCanShowOther(false);
                    setErrorInfo(errorState);
                } else {
                    amount = updatedAmount;
                }
            }
            setErrorInfo({ title: "", subtitle: "", show: false });
            setSelectedTipAmount(amount);
            if (typeof onUpdateTipAmount === "function" && amount >= 0) {
                onUpdateTipAmount(amount);
            }
        },
        [onUpdateTipAmount, tipWarnMessage, minValidTipAmount, maxValidTipAmount, setErrorInfo],
    );

    const clearOtherTipAmount = useCallback(() => {
        setCanShowOther(false);
        setErrorInfo({ title: "", subtitle: "", show: false });
        validateSelectedTipAmount();
    }, [validateSelectedTipAmount, setErrorInfo]);

    const updateTipAmount = useCallback(
        (updatedAmount?: string) => {
            if (!updatedAmount) {
                clearOtherTipAmount();
                return;
            }
            const otherSelectedTipAmount = Number(updatedAmount);
            const amount = isNaN(otherSelectedTipAmount) ? 0 : otherSelectedTipAmount;

            let _amount = selectedAmount;

            //checking for error state before updating
            const errorState = getErrorInfo(
                otherSelectedTipAmount,
                tipWarnMessage,
                minValidTipAmount,
                maxValidTipAmount,
            );

            if (errorState?.show) {
                _amount = selectedAmount;
            } else {
                _amount = amount;
            }
            setErrorInfo(errorState);
            setSelectedTipAmount(_amount);

            const isTipOtherAmount = isOtherAmount(_amount, tipOptions);
            if (isTipOtherAmount) {
                setCanShowOther(true);
            } else {
                setCanShowOther(false);
            }

            setErrorInfo({ title: "", subtitle: "", show: false });

            if (typeof onUpdateTipAmount === "function" && _amount >= 0 && _amount != selectedAmount) {
                onUpdateTipAmount(_amount);
            }

            // analytics event for custom tip amount
            clickCustomTipAmount(optInBool, amount);
        },
        [
            tipOptions,
            clickCustomTipAmount,
            optInBool,
            clearOtherTipAmount,
            selectedAmount,
            onUpdateTipAmount,
            maxValidTipAmount,
            minValidTipAmount,
            tipWarnMessage,
            setErrorInfo,
        ],
    );

    const onInputChange = useCallback(
        (amount: string | undefined) => {
            const errorState = getErrorInfo(Number(amount), tipWarnMessage, minValidTipAmount, maxValidTipAmount);
            if (errorState?.show) {
                setErrorInfo(errorState);
            } else {
                setErrorInfo({ title: "", subtitle: "", show: false });
            }
        },
        [maxValidTipAmount, minValidTipAmount, tipWarnMessage, setErrorInfo],
    );

    const onTipSelection = useCallback(
        ({ selected, amount }: OnTipBoxSelectionParams) => {
            validateSelectedTipAmount(selected ? amount : 0);
            setErrorInfo({ title: "", subtitle: "", show: false });

            if (amount === OTHER_AMOUNT) {
                setCanShowOther(selected);
            } else {
                clickPredefinedTipAmount(optInBool, amount);
                setCanShowOther(false);
            }
        },
        [validateSelectedTipAmount, clickPredefinedTipAmount, optInBool, setErrorInfo],
    );

    return (
        <div className={className}>
            <TipsWrapper
                onTipBoxSelection={onTipSelection}
                selectedTipAmount={selectedTipAmount}
                tipOptions={tipOptions}
            />
            {canShowOther && (
                <TipInputBox
                    amount={otherAmount}
                    onUpdateTipAmount={updateTipAmount}
                    onClearTipAmount={clearOtherTipAmount}
                    onInputBlur={onInputBlur}
                    onInputFocus={onInputFocus}
                    scrollIntoView={inputScrollIntoView}
                    // Passing states from modal hook for showing errors on UX4 Input field
                    onInputChange={onInputChange}
                    showError={error?.show}
                    errorMessage={`${error?.title} ${error?.subtitle}`}
                    tipWarnMessage={tipWarnMessage}
                    minValidTipAmount={minValidTipAmount}
                    maxValidTipAmount={maxValidTipAmount}
                />
            )}
        </div>
    );
};

export default memo(TippingWidget);
